/** Express Entry → strategy (D-080): position against the latest French-language rounds, ranked
 * recommendations computed by rules, and the AI plan written from those facts (reviewed by the advisor). */

import {
  useImmigrationDossiersStrategyCreate,
  useImmigrationDossiersStrategyRetrieve,
  useImmigrationDossiersStrategyReviewCreate,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { localized } from "../../components/TranslatedFields";

interface Recommendation {
  key: string;
  priority: number;
  params: Record<string, unknown>;
}
interface Facts {
  crs: number;
  french_level: number | null;
  recent_cutoffs: number[];
  recent_median: number | null;
  last_cutoff: number | null;
  above_recent: number;
  rounds: { number: number; held_on: string; invitations: number; cutoff: number }[];
  fsw_eligible: "ok" | "ko" | "unknown";
  tables_verified: boolean;
}
interface AiPlan {
  summary?: string;
  plan?: { step: string; why: string; when?: string }[];
  risks?: string[];
  questions?: string[];
}
interface Saved {
  id: string;
  recommendations: Recommendation[];
  ai_plan: AiPlan;
  ai_status: "pending" | "done" | "unavailable" | "rejected";
  created_by_name: string;
  reviewed_by_name: string;
  reviewed_at: string | null;
  created_at: string;
}
interface Payload {
  live: { facts: Facts; recommendations: Recommendation[] };
  latest: Saved | null;
}

const PRIORITY_TONE = { 1: "danger", 2: "offer", 3: "default" } as const;

/** Parameters ready for the sentence: lists and lever / requirement keys translated. */
function useParams() {
  const { t, i18n } = useTranslation();
  return (rec: Recommendation): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(rec.params)) {
      if (key === "requirement") out[key] = t(`fsw.req.${String(value)}`);
      else if (key === "lever") out[key] = t(`ee.lever.${String(value)}`);
      else if (key === "deadline") out[key] = t(`fsw.deadline.${String(value)}`);
      else if (key === "items") out[key] = (value as string[]).map((v) => t(`fsw.req.${v}`)).join(", ");
      else if (key === "levers") out[key] = (value as string[]).map((v) => t(`ee.lever.${v}`)).join(" · ");
      else if (key === "streams")
        out[key] = (value as { name: Record<string, string> }[]).map((stream) => localized(stream.name, i18n.language)).join(" · ");
      else if (key === "state") out[key] = t(`strategy.state.${String(value)}`);
      else if (key === "fields") out[key] = (value as string[]).map((v) => t(`strategy.spouseField.${v}`)).join(", ");
      else if (value === null) out[key] = "—";
      else out[key] = String(value);
    }
    return out;
  };
}

export function Strategy({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () =>
    void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/immigration/") });
  const strategy = useImmigrationDossiersStrategyRetrieve(id, {
    query: {
      staleTime: 0,
      // While the AI writes the plan, check again every few seconds.
      refetchInterval: (query) => {
        const data = query.state.data as { status: number; data: unknown } | undefined;
        const latest = data?.status === 200 ? (data.data as Payload).latest : null;
        return latest?.ai_status === "pending" ? 4000 : false;
      },
    },
  });
  const generate = useImmigrationDossiersStrategyCreate({ mutation: { onSuccess: refresh } });
  const review = useImmigrationDossiersStrategyReviewCreate({ mutation: { onSuccess: refresh } });
  const params = useParams();
  if (strategy.isPending) return <Skeleton style={{ height: 240 }} />;
  if (strategy.data?.status !== 200) return <GlassCard role="alert">{t("common.error")}</GlassCard>;
  const { live, latest } = strategy.data.data as unknown as Payload;
  const facts = live.facts;
  const language = i18n.language === "en" ? "en" : "fr";
  const locale = language === "en" ? "en-GB" : "fr";
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale, { timeZone: "Africa/Douala" });
  const gap = facts.recent_median === null ? null : facts.crs - facts.recent_median;

  return (
    <div className="stack">
      {facts.tables_verified ? null : (
        <p role="alert" className="notice-warning ao-glass ao-card">
          <CircleAlert size={16} aria-hidden="true" /> {t("fsw.tablesNotVerified")}
        </p>
      )}

      <section className="kpis" aria-label={t("strategy.position")}>
        <GlassCard className="kpi__link">
          <span className="kpi__label">{t("strategy.crs")}</span>
          <span className="kpi__value">{facts.crs}</span>
        </GlassCard>
        <GlassCard className="kpi__link">
          <span className="kpi__label">{t("strategy.french")}</span>
          <span className="kpi__value">{facts.french_level === null ? "—" : `NCLC ${facts.french_level}`}</span>
        </GlassCard>
        <GlassCard className="kpi__link">
          <span className="kpi__label">{t("strategy.median", { count: facts.recent_cutoffs.length })}</span>
          <span className="kpi__value">{facts.recent_median ?? "—"}</span>
        </GlassCard>
        <GlassCard className="kpi__link">
          <span className="kpi__label">{t("strategy.gap")}</span>
          <span className={`kpi__value ${gap !== null && gap < 0 ? "ko" : "ok"}`}>{gap === null ? "—" : gap > 0 ? `+${gap}` : gap}</span>
          <span className="kpi__hint">{t("strategy.above", { above: facts.above_recent, count: facts.recent_cutoffs.length })}</span>
        </GlassCard>
      </section>

      <div className="grid">
        <GlassCard as="section" className="stack">
          <h2>{t("strategy.recommendations")}</h2>
          <p className="muted">{t("strategy.recommendationsIntro")}</p>
          <ol className="checks">
            {live.recommendations.map((rec, index) => (
              <li key={`${rec.key}-${index}`} className="check">
                <span className="muted">{index + 1}</span>
                <span>
                  <strong>{t(`strategy.rec.${rec.key}.title`, params(rec))}</strong>
                  <span className="check__detail"> · {t(`strategy.rec.${rec.key}.why`, params(rec))}</span>
                </span>
                <Badge tone={PRIORITY_TONE[rec.priority as 1 | 2 | 3] ?? "default"}>{t(`strategy.priority.${rec.priority}`)}</Badge>
              </li>
            ))}
          </ol>
        </GlassCard>

        <GlassCard as="section" className="stack">
          <h2>{t("strategy.rounds")}</h2>
          {facts.rounds.length ? (
            <table className="table">
              <thead>
                <tr>
                  <th>{t("strategy.round")}</th>
                  <th>{t("strategy.date")}</th>
                  <th>{t("strategy.invitations")}</th>
                  <th>{t("strategy.cutoff")}</th>
                </tr>
              </thead>
              <tbody>
                {facts.rounds.slice(0, 8).map((round) => (
                  <tr key={round.number}>
                    <td>#{round.number}</td>
                    <td>{date(round.held_on)}</td>
                    <td>{round.invitations}</td>
                    <td>
                      <Badge tone={facts.crs >= round.cutoff ? "success" : "default"}>{round.cutoff}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted">{t("strategy.noRounds")}</p>
          )}
          <p className="check__detail">{t("strategy.roundsNote")}</p>
        </GlassCard>
      </div>

      <GlassCard as="section" className="stack">
        <div className="section-title">
          <div>
            <h2>
              <Sparkles size={16} aria-hidden="true" /> {t("strategy.aiTitle")}
            </h2>
            <p className="muted">{t("strategy.aiIntro")}</p>
          </div>
          <Button disabled={generate.isPending || latest?.ai_status === "pending"} onClick={() => generate.mutate({ id, data: { language } })}>
            {latest ? t("strategy.regenerate") : t("strategy.generate")}
          </Button>
        </div>
        {latest ? (
          <div className="stack">
            <p className="check__detail">
              {t("strategy.savedBy", { name: latest.created_by_name || "—", date: date(latest.created_at) })} · {t(`strategy.aiStatus.${latest.ai_status}`)}
            </p>
            {latest.ai_status === "pending" ? <Skeleton style={{ height: 80 }} /> : null}
            {latest.ai_status === "done" ? (
              <>
                <p>{latest.ai_plan.summary}</p>
                <ol className="stack">
                  {(latest.ai_plan.plan ?? []).map((step, index) => (
                    <li key={index}>
                      <strong>{step.step}</strong>
                      {step.when ? <span className="muted"> · {step.when}</span> : null}
                      <br />
                      <span className="check__detail">{step.why}</span>
                    </li>
                  ))}
                </ol>
                {latest.ai_plan.risks?.length ? (
                  <>
                    <h3>{t("strategy.risks")}</h3>
                    <ul>
                      {latest.ai_plan.risks.map((risk) => (
                        <li key={risk}>{risk}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {latest.ai_plan.questions?.length ? (
                  <>
                    <h3>{t("strategy.questions")}</h3>
                    <ul>
                      {latest.ai_plan.questions.map((question) => (
                        <li key={question}>{question}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </>
            ) : null}
            {latest.ai_status === "unavailable" || latest.ai_status === "rejected" ? (
              <p className="muted">{t(`strategy.aiFallback.${latest.ai_status}`)}</p>
            ) : null}
            {latest.reviewed_at ? (
              <p className="ok">{t("strategy.reviewed", { name: latest.reviewed_by_name, date: date(latest.reviewed_at) })}</p>
            ) : (
              <div className="row">
                <Button variant="secondary" disabled={review.isPending || latest.ai_status === "pending"} onClick={() => review.mutate({ id })}>
                  {t("strategy.markReviewed")}
                </Button>
                <span className="check__detail">{t("strategy.reviewNote")}</span>
              </div>
            )}
          </div>
        ) : (
          <p className="muted">{t("strategy.none")}</p>
        )}
      </GlassCard>
      <p role="note" className="check__detail">
        {t("fsw.disclaimer")}
      </p>
    </div>
  );
}
